import type {
  DocumentPageMetadataUserEditValueKey,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { DOCUMENT_PAGE_METADATA_USER_EDIT_VALUE_KEYS } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { MetadataEditScope } from 'features/knowledgeDocumentViewer/types/metadataEditScope'
import { isBlankMetadataValue } from 'features/knowledgeDocumentViewer/utils/metadataUserEditWrite'
import { pageRevisionFamilyKey } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'

export type PageMetadataFormVisibility =
  | 'editable'
  | 'hidden'
  | 'family-locked'
  | 'eval-only'

/**
 * Single source of truth for page-metadata labels, schema keys, and form policy.
 * Display lists and draft↔schema mapping both derive from this.
 */
export const PAGE_METADATA_FIELD_CONFIG = [
  { label: 'Page', form: 'hidden' },
  { label: 'Type', key: 'page_classification', form: 'editable' },
  { label: 'Drawing #', key: 'issuer_drawing_number', form: 'editable' },
  { label: 'Sheet #', key: 'sheet_number', form: 'editable' },
  { label: 'Revision Code', key: 'latest_revision', form: 'family-locked' },
  {
    label: 'Revision Date',
    key: 'latest_revision_date',
    form: 'family-locked',
  },
  { label: 'System', key: 'system', form: 'editable' },
  { label: 'Vendor', key: 'issuer_company_name', form: 'editable' },
  {
    label: 'Viewing Rotation',
    key: 'viewing_rotation',
    form: 'eval-only',
  },
  {
    label: 'Revision-controlled Drawing',
    key: 'is_revision_controlled_drawing',
    form: 'eval-only',
  },
  { label: 'Design Facility', key: 'design_facility', form: 'eval-only' },
  { label: 'Title', key: 'document_title', form: 'hidden' },
] as const

type PageMetadataFieldConfigEntry = (typeof PAGE_METADATA_FIELD_CONFIG)[number]

export type PageMetadataFieldLabel = PageMetadataFieldConfigEntry['label']

export const PAGE_REVISION_CODE_FIELD_LABEL: PageMetadataFieldLabel =
  'Revision Code'

export type PageMetadataMappedLabel = Extract<
  PageMetadataFieldConfigEntry,
  { key: DocumentPageMetadataUserEditValueKey }
>['label']

export type PageMetadataEditableLabel = Extract<
  PageMetadataFieldConfigEntry,
  { form: 'editable' | 'family-locked' | 'eval-only' }
>['label']

/** Draft map keyed only by editable page-metadata labels. */
export type PageMetadataDraftFields = Partial<
  Record<PageMetadataEditableLabel, string>
>

function buildPageMetadataLabelToKey(): Record<
  string,
  DocumentPageMetadataUserEditValueKey
> {
  const map: Record<string, DocumentPageMetadataUserEditValueKey> = {}
  for (const field of PAGE_METADATA_FIELD_CONFIG) {
    if ('key' in field) {
      map[field.label] = field.key
    }
  }
  return map
}

export const PAGE_METADATA_LABEL_TO_KEY = buildPageMetadataLabelToKey()

/** Omitted from every page form (sidebar and eval). */
export const HIDDEN_PAGE_METADATA_LABELS = new Set<string>(
  PAGE_METADATA_FIELD_CONFIG.filter((field) => field.form === 'hidden').map(
    (field) => field.label
  )
)

/** Editable in metadata evals; omitted from the customer sidebar. */
export const EVAL_ONLY_PAGE_METADATA_LABELS = new Set<string>(
  PAGE_METADATA_FIELD_CONFIG.filter((field) => field.form === 'eval-only').map(
    (field) => field.label
  )
)

/** Omitted from the document-viewer sidebar form and read-only preview. */
export const SIDEBAR_HIDDEN_PAGE_METADATA_LABELS = new Set<string>([
  ...HIDDEN_PAGE_METADATA_LABELS,
  ...EVAL_ONLY_PAGE_METADATA_LABELS,
])

/** Shown disabled in "edit all revisions" scope. */
export const FAMILY_SCOPE_LOCKED_PAGE_METADATA_LABELS = new Set<string>(
  PAGE_METADATA_FIELD_CONFIG.filter(
    (field) => field.form === 'family-locked'
  ).map((field) => field.label)
)

const NO_SCOPE_LOCKED_PAGE_METADATA_LABELS: ReadonlySet<string> = new Set()

export const DRAWING_NUMBER_FIELD_LABEL = 'Drawing #'
export const SHEET_NUMBER_FIELD_LABEL = 'Sheet #'

/**
 * UI fields that fan out on family save: editable labels that are neither
 * hidden nor locked in family scope.
 */
export const FAMILY_SHARED_PAGE_METADATA_VALUE_KEYS =
  PAGE_METADATA_FIELD_CONFIG.flatMap((field) =>
    field.form === 'editable' && 'key' in field ? [field.key] : []
  )

/** Human page-metadata keys (eval empty-gate). Excludes provenance like decision method. */
export const PAGE_METADATA_LABEL_VALUE_KEYS =
  PAGE_METADATA_FIELD_CONFIG.flatMap((field) =>
    'key' in field ? [field.key] : []
  )

export function arePageMetadataLabelValuesEmpty(
  values: DocumentPageMetadataUserEditValues
): boolean {
  return PAGE_METADATA_LABEL_VALUE_KEYS.every((key) =>
    isBlankMetadataValue(values[key])
  )
}

export function lockedPageMetadataLabelsForScope(
  editScope: MetadataEditScope | undefined
): ReadonlySet<string> {
  if (editScope === 'family') {
    return FAMILY_SCOPE_LOCKED_PAGE_METADATA_LABELS
  }
  return NO_SCOPE_LOCKED_PAGE_METADATA_LABELS
}

export function isPageMetadataEditableLabel(
  label: string
): label is PageMetadataEditableLabel {
  return (
    Object.prototype.hasOwnProperty.call(PAGE_METADATA_LABEL_TO_KEY, label) &&
    !HIDDEN_PAGE_METADATA_LABELS.has(label)
  )
}

export function pageMetadataLabelToKey(
  label: string
): DocumentPageMetadataUserEditValueKey | undefined {
  if (!isPageMetadataEditableLabel(label)) return undefined
  return PAGE_METADATA_LABEL_TO_KEY[label]
}

export function draftLabelsToPageValues(
  draftFields: PageMetadataDraftFields,
  baseline: DocumentPageMetadataUserEditValues,
  lockedLabels: ReadonlySet<string> = NO_SCOPE_LOCKED_PAGE_METADATA_LABELS
): DocumentPageMetadataUserEditValues {
  const next: DocumentPageMetadataUserEditValues = {}
  for (const key of DOCUMENT_PAGE_METADATA_USER_EDIT_VALUE_KEYS) {
    next[key] = baseline[key] ?? null
  }
  for (const [label, value] of Object.entries(draftFields)) {
    if (value === undefined) continue
    if (HIDDEN_PAGE_METADATA_LABELS.has(label)) continue
    if (lockedLabels.has(label)) continue
    if (!isPageMetadataEditableLabel(label)) continue
    next[PAGE_METADATA_LABEL_TO_KEY[label]] = isBlankMetadataValue(value)
      ? null
      : value
  }
  return next
}

export function toEditablePageDraftFields(
  fields: readonly { label: string; value: string }[],
  hiddenLabels: ReadonlySet<string> = SIDEBAR_HIDDEN_PAGE_METADATA_LABELS
): PageMetadataDraftFields {
  const draft: PageMetadataDraftFields = {}
  for (const field of fields) {
    if (hiddenLabels.has(field.label)) continue
    if (!isPageMetadataEditableLabel(field.label)) continue
    draft[field.label] = field.value
  }
  return draft
}

export function isLeaveFamilyKeyChanged(args: {
  draftDrawingNumber: string | undefined
  originalDrawingNumber: string | undefined
  draftSheetNumber: string | undefined
  originalSheetNumber: string | undefined
}): boolean {
  return (
    pageRevisionFamilyKey(args.draftDrawingNumber, args.draftSheetNumber) !==
    pageRevisionFamilyKey(args.originalDrawingNumber, args.originalSheetNumber)
  )
}
